import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  IconCheck,
  IconX,
  IconArrowDownLeft,
  IconFlame,
  IconCalculator,
  IconRotate,
  IconAlertCircle,
  IconAlertTriangle,
  IconBox,
  IconInfoCircle,
  IconReceipt,
} from "@tabler/icons-react";
import { iconStroke } from "../../config/config";
import { settleAndReconcileCustody } from "../../controllers/item_issue.controller";
import toast from "react-hot-toast";

export default function UnifiedSettleReconcileModal({
  isOpen,
  onClose,
  custodyRecord,
  eligibleItems,
  onSuccess,
}) {
  const [posSaleNumber, setPosSaleNumber] = useState("");
  const [posSaleQty, setPosSaleQty] = useState("");
  const [posSaleUnit, setPosSaleUnit] = useState(null);
  const [posSaleNotes, setPosSaleNotes] = useState("");

  const [returnQty, setReturnQty] = useState("");
  const [returnUnit, setReturnUnit] = useState(null);
  const [returnNotes, setReturnNotes] = useState("");

  const [consumptionQty, setConsumptionQty] = useState("");
  const [consumptionUnit, setConsumptionUnit] = useState(null);
  const [consumptionType, setConsumptionType] = useState("wastage");
  const [consumptionReason, setConsumptionReason] = useState("");
  const [consumptionNotes, setConsumptionNotes] = useState("");

  const [actualPhysicalCount, setActualPhysicalCount] = useState("0");
  const [actualUnit, setActualUnit] = useState(null);
  const [reconciliationReason, setReconciliationReason] = useState("End of shift audit & settlement");

  const [closeCustody, setCloseCustody] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Matching master inventory item
  const matchingMasterItem = useMemo(() => {
    if (!custodyRecord) return null;
    return (eligibleItems || []).find(
      (i) => i.id === custodyRecord.inventory_item_id
    ) || null;
  }, [custodyRecord, eligibleItems]);

  // Unit options
  const unitOptions = useMemo(() => {
    const baseUnit = custodyRecord?.unit || matchingMasterItem?.unit || "Unit";
    const list = [
      {
        value: `base_${baseUnit}`,
        label: `${baseUnit} (Base Unit - 1.0x)`,
        unit_name: baseUnit,
        purchase_unit_id: null,
        conversion_factor: 1.0,
      },
    ];

    if (matchingMasterItem && Array.isArray(matchingMasterItem.alternate_units)) {
      matchingMasterItem.alternate_units.forEach((u) => {
        list.push({
          value: `alt_${u.purchase_unit_id}`,
          label: `${u.unit_name} (${u.conversion_factor} ${baseUnit} per ${u.unit_name})`,
          unit_name: u.unit_name,
          purchase_unit_id: u.purchase_unit_id,
          conversion_factor: parseFloat(u.conversion_factor) || 1.0,
        });
      });
    }

    return list;
  }, [custodyRecord, matchingMasterItem]);

  // Reset & initialize form whenever modal opens or record changes
  useEffect(() => {
    if (isOpen && custodyRecord) {
      setPosSaleNumber("");
      setPosSaleQty("");
      setPosSaleNotes("");
      setReturnQty("");
      setReturnNotes("");
      setConsumptionQty("");
      setConsumptionType("wastage");
      setConsumptionReason("");
      setConsumptionNotes("");
      setActualPhysicalCount("0");
      setReconciliationReason("End of shift audit & settlement");
      setCloseCustody(true);

      const baseOption = unitOptions[0] || null;
      setPosSaleUnit(baseOption);
      setReturnUnit(baseOption);
      setConsumptionUnit(baseOption);
      setActualUnit(baseOption);
    }
  }, [isOpen, custodyRecord, unitOptions]);

  // Dynamic calculations
  const initialCustody = parseFloat(custodyRecord?.remaining_base_quantity) || 0;
  const pendingSales = parseFloat(custodyRecord?.pending_sales_base_quantity) || 0;
  const baseUnitName = custodyRecord?.unit || "Unit";

  const posFactor = posSaleUnit?.conversion_factor || 1.0;
  const posSaleBaseQty = (parseFloat(posSaleQty) || 0) * posFactor;

  const retFactor = returnUnit?.conversion_factor || 1.0;
  const returnBaseQty = (parseFloat(returnQty) || 0) * retFactor;

  const conFactor = consumptionUnit?.conversion_factor || 1.0;
  const consumeBaseQty = (parseFloat(consumptionQty) || 0) * conFactor;

  const expectedRemaining = Math.max(
    0,
    initialCustody - posSaleBaseQty - returnBaseQty - consumeBaseQty - pendingSales
  );

  const actFactor = actualUnit?.conversion_factor || 1.0;
  const actualBaseQty = (parseFloat(actualPhysicalCount) || 0) * actFactor;

  const difference = actualBaseQty - expectedRemaining;

  // Auto check close custody when actual count is 0
  const handleActualCountChange = (val) => {
    setActualPhysicalCount(val);
    const num = parseFloat(val) || 0;
    if (num <= 0.0001) {
      setCloseCustody(true);
    }
  };

  // Validation
  const posSaleExceeds = posSaleBaseQty > initialCustody + 0.0001;
  const returnExceeds = returnBaseQty > Math.max(0, initialCustody - posSaleBaseQty) + 0.0001;
  const consumeExceeds = consumeBaseQty > Math.max(0, initialCustody - posSaleBaseQty - returnBaseQty) + 0.0001;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!custodyRecord) return;

    if (posSaleExceeds) {
      toast.error(`POS Sale quantity exceeds available custody balance (${initialCustody.toFixed(2)} ${baseUnitName}).`);
      return;
    }
    if (returnExceeds) {
      toast.error(`Return quantity exceeds available custody balance after POS sales.`);
      return;
    }
    if (consumeExceeds) {
      toast.error(`Wastage quantity exceeds remaining custody after return & sales.`);
      return;
    }
    if (actualBaseQty < 0) {
      toast.error("Physical count cannot be negative.");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        user_id: custodyRecord.user_id,
        employee_name: custodyRecord.employee_name || custodyRecord.user_id,
        department: custodyRecord.department,
        inventory_item_id: custodyRecord.inventory_item_id,
        issue_id: custodyRecord.issue_id || custodyRecord.id,
        pos_sale_number: posSaleNumber.trim() || null,
        pos_sale_reference: posSaleNumber.trim() || null,
        pos_sale_quantity: parseFloat(posSaleQty) || 0,
        pos_sale_purchase_unit_id: posSaleUnit?.purchase_unit_id || null,
        pos_sale_unit_name: posSaleUnit?.unit_name || baseUnitName,
        pos_sale_notes: posSaleNotes || null,
        return_quantity: parseFloat(returnQty) || 0,
        return_purchase_unit_id: returnUnit?.purchase_unit_id || null,
        return_unit_name: returnUnit?.unit_name || baseUnitName,
        return_notes: returnNotes || null,
        consumption_quantity: parseFloat(consumptionQty) || 0,
        consumption_purchase_unit_id: consumptionUnit?.purchase_unit_id || null,
        consumption_unit_name: consumptionUnit?.unit_name || baseUnitName,
        consumption_type: consumptionType,
        consumption_reason: consumptionReason || "Non-sale usage / wastage",
        consumption_notes: consumptionNotes || null,
        actual_physical_quantity: parseFloat(actualPhysicalCount) || 0,
        actual_purchase_unit_id: actualUnit?.purchase_unit_id || null,
        actual_unit_name: actualUnit?.unit_name || baseUnitName,
        reconciliation_reason: reconciliationReason || "Audit & Settlement",
        close_custody: closeCustody,
      };

      const res = await settleAndReconcileCustody(payload);
      if (res.data?.success) {
        toast.success(res.data.message || "Custody settled and reconciled successfully!");
        if (onSuccess) onSuccess();
        onClose();
      } else {
        toast.error(res.data?.message || "Settlement failed.");
      }
    } catch (err) {
      console.error("Unified settle error:", err);
      toast.error(err?.response?.data?.message || err.message || "Failed to process settlement.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !custodyRecord) return null;

  return (
    <dialog className="modal modal-open z-50">
      <div className="modal-box w-11/12 max-w-5xl p-6 bg-base-100 rounded-2xl border border-base-200 shadow-2xl overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-base-200">
          <div>
            <h3 className="font-bold text-lg text-base-content flex items-center gap-2">
              <IconCalculator className="text-primary" size={22} stroke={iconStroke} />
              Custody Audit, Return & Reconciliation
            </h3>
            <p className="text-xs text-base-content/60 mt-0.5">
              Record POS sales reference, physical return to store, non-sale wastage, and audit remaining stock in a single unified step.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-sm btn-circle btn-ghost text-base-content/60 hover:text-base-content"
          >
            <IconX size={20} stroke={iconStroke} />
          </button>
        </div>

        {/* Custody Info Banner */}
        <div className="my-4 bg-base-200/50 rounded-xl p-4 border border-base-200 grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
          <div>
            <span className="text-base-content/50 block">Custodian / Staff</span>
            <span className="font-semibold text-base-content text-sm block truncate">
              {custodyRecord.employee_name || custodyRecord.user_id}
            </span>
            <span className="text-[10px] text-base-content/60 block">
              Dept: {custodyRecord.department || "Staff"}
            </span>
          </div>
          <div>
            <span className="text-base-content/50 block">Product</span>
            <span className="font-semibold text-base-content text-sm block truncate">
              {custodyRecord.item_title}
            </span>
            <span className="text-[10px] text-base-content/60 block">
              Issue #{custodyRecord.issue_no || custodyRecord.id}
            </span>
          </div>
          <div>
            <span className="text-base-content/50 block">Total Issued</span>
            <span className="font-semibold text-base-content block">
              {custodyRecord.base_quantity} {baseUnitName}
            </span>
            <span className="text-[10px] text-info block">
              Sold: {custodyRecord.settled_base_quantity || 0} {baseUnitName}
            </span>
          </div>
          <div className="bg-primary/10 rounded-lg p-2 border border-primary/20">
            <span className="text-primary font-medium block">Active Custody Balance</span>
            <span className="font-bold text-primary text-base block font-mono">
              {initialCustody.toLocaleString()} {baseUnitName}
            </span>
            {pendingSales > 0 && (
              <span className="text-[10px] text-warning block font-medium">
                Pending Sales: {pendingSales} {baseUnitName}
              </span>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* 1. POS Sales Record */}
            <div className="bg-base-200/30 rounded-xl p-4 border border-base-200 space-y-3">
              <div className="flex items-center gap-2 text-info font-semibold text-sm">
                <IconReceipt size={18} stroke={iconStroke} />
                <span>1. POS Sales Record (Optional)</span>
              </div>
              <p className="text-[11px] text-base-content/60">
                Informational reference for custody balance only. Does <span className="font-semibold text-info">not</span> deduct warehouse stock.
              </p>

              <div>
                <label className="label py-1">
                  <span className="label-text text-xs">POS Bill / Sale #</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. INV-1002, POS-042"
                  className="input input-sm input-bordered w-full text-xs font-mono"
                  value={posSaleNumber}
                  onChange={(e) => setPosSaleNumber(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="label py-1">
                    <span className="label-text text-xs">Sold Qty</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    placeholder="0.00"
                    className={`input input-sm input-bordered w-full text-xs ${
                      posSaleExceeds ? "input-error" : ""
                    }`}
                    value={posSaleQty}
                    onChange={(e) => setPosSaleQty(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label py-1">
                    <span className="label-text text-xs">Unit</span>
                  </label>
                  <Select
                    className="text-xs"
                    value={posSaleUnit}
                    onChange={setPosSaleUnit}
                    options={unitOptions}
                    isSearchable={false}
                  />
                </div>
              </div>

              {posSaleBaseQty > 0 && (
                <div className="text-[11px] font-mono text-info">
                  = {posSaleBaseQty.toFixed(2)} {baseUnitName} accounted as POS sales
                </div>
              )}
              {posSaleExceeds && (
                <div className="text-[11px] text-error flex items-center gap-1">
                  <IconAlertTriangle size={14} /> Exceeds custody balance!
                </div>
              )}

              <div>
                <label className="label py-1">
                  <span className="label-text text-xs">Sales Notes (Optional)</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Cash sale counter bill"
                  className="input input-sm input-bordered w-full text-xs"
                  value={posSaleNotes}
                  onChange={(e) => setPosSaleNotes(e.target.value)}
                />
              </div>
            </div>

            {/* 2. Return Custody Items */}
            <div className="bg-base-200/30 rounded-xl p-4 border border-base-200 space-y-3">
              <div className="flex items-center gap-2 text-success font-semibold text-sm">
                <IconArrowDownLeft size={18} stroke={iconStroke} />
                <span>2. Return Custody Items</span>
              </div>
              <p className="text-[11px] text-base-content/60">
                Quantity of intact items returning back from staff custody.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="label py-1">
                    <span className="label-text text-xs">Return Qty</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    placeholder="0.00"
                    className={`input input-sm input-bordered w-full text-xs ${
                      returnExceeds ? "input-error" : ""
                    }`}
                    value={returnQty}
                    onChange={(e) => setReturnQty(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label py-1">
                    <span className="label-text text-xs">Unit</span>
                  </label>
                  <Select
                    className="text-xs"
                    value={returnUnit}
                    onChange={setReturnUnit}
                    options={unitOptions}
                    isSearchable={false}
                  />
                </div>
              </div>

              {returnBaseQty > 0 && (
                <div className="text-[11px] font-mono text-success">
                  = {returnBaseQty.toFixed(2)} {baseUnitName} returned from staff custody
                </div>
              )}
              {returnExceeds && (
                <div className="text-[11px] text-error flex items-center gap-1">
                  <IconAlertTriangle size={14} /> Exceeds custody balance!
                </div>
              )}

              <div>
                <label className="label py-1">
                  <span className="label-text text-xs">Return Notes (Optional)</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Unopened bottles returned from staff"
                  className="input input-sm input-bordered w-full text-xs"
                  value={returnNotes}
                  onChange={(e) => setReturnNotes(e.target.value)}
                />
              </div>
            </div>

            {/* 3. Non-Sale Usage / Wastage */}
            <div className="bg-base-200/30 rounded-xl p-4 border border-base-200 space-y-3">
              <div className="flex items-center gap-2 text-warning font-semibold text-sm">
                <IconFlame size={18} stroke={iconStroke} />
                <span>3. Non-Sale Usage / Wastage</span>
              </div>
              <p className="text-[11px] text-base-content/60">
                Quantity consumed internally, broken, spoiled, or spilled in custody.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="label py-1">
                    <span className="label-text text-xs">Wastage Qty</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    placeholder="0.00"
                    className={`input input-sm input-bordered w-full text-xs ${
                      consumeExceeds ? "input-error" : ""
                    }`}
                    value={consumptionQty}
                    onChange={(e) => setConsumptionQty(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label py-1">
                    <span className="label-text text-xs">Unit</span>
                  </label>
                  <Select
                    className="text-xs"
                    value={consumptionUnit}
                    onChange={setConsumptionUnit}
                    options={unitOptions}
                    isSearchable={false}
                  />
                </div>
              </div>

              {consumeBaseQty > 0 && (
                <div className="text-[11px] font-mono text-warning">
                  = {consumeBaseQty.toFixed(2)} {baseUnitName} recorded as custody wastage
                </div>
              )}
              {consumeExceeds && (
                <div className="text-[11px] text-error flex items-center gap-1">
                  <IconAlertTriangle size={14} /> Exceeds remaining balance!
                </div>
              )}

              <div className="space-y-2">
                <div>
                  <label className="label py-1">
                    <span className="label-text text-xs">Usage Type</span>
                  </label>
                  <select
                    className="select select-sm select-bordered w-full text-xs"
                    value={consumptionType}
                    onChange={(e) => setConsumptionType(e.target.value)}
                  >
                    <option value="wastage">General Wastage</option>
                    <option value="breakage">Breakage / Damage</option>
                    <option value="tasting">Tasting / Sampling</option>
                    <option value="staff_meal">Staff Meal</option>
                    <option value="cleaning">Cleaning / Sanitizing</option>
                    <option value="expired">Expired / Spoiled</option>
                  </select>
                </div>
                <div>
                  <label className="label py-1">
                    <span className="label-text text-xs">Reason / Note</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Broken at bar counter"
                    className="input input-sm input-bordered w-full text-xs"
                    value={consumptionReason}
                    onChange={(e) => setConsumptionReason(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 4. Physical Count Found & Reconcile */}
          <div className="bg-base-200/50 rounded-xl p-4 border border-base-200 space-y-3">
            <div className="flex items-center gap-2 text-primary font-semibold text-sm">
              <IconCheck size={18} stroke={iconStroke} />
              <span>4. Physical Audit: Actual Stock Found Remaining with Staff</span>
            </div>
            <p className="text-[11px] text-base-content/60">
              Enter the exact count remaining in the custodian's hand. If they returned all stock or this is a final shift closure, enter 0.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="label py-1">
                  <span className="label-text text-xs font-semibold">Actual Physical Count</span>
                </label>
                <input
                  type="number"
                  step="any"
                  min="0"
                  placeholder="0.00"
                  className="input input-sm input-bordered w-full text-xs font-bold font-mono"
                  value={actualPhysicalCount}
                  onChange={(e) => handleActualCountChange(e.target.value)}
                />
              </div>
              <div>
                <label className="label py-1">
                  <span className="label-text text-xs">Unit</span>
                </label>
                <Select
                  className="text-xs"
                  value={actualUnit}
                  onChange={setActualUnit}
                  options={unitOptions}
                  isSearchable={false}
                />
              </div>
              <div>
                <label className="label py-1">
                  <span className="label-text text-xs">Audit Reason / Remarks</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. End of shift settlement"
                  className="input input-sm input-bordered w-full text-xs"
                  value={reconciliationReason}
                  onChange={(e) => setReconciliationReason(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Live Dynamic Calculator & Discrepancy Breakdown */}
          <div className="bg-base-100 rounded-xl p-4 border-2 border-primary/20 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-bold text-xs text-base-content">
                <IconCalculator size={16} className="text-primary" />
                Live Dynamic Reconciliation Calculator
              </div>
              <div>
                {Math.abs(difference) <= 0.0001 ? (
                  <span className="badge badge-success gap-1 text-xs py-2 px-3 font-semibold text-white">
                    <IconCheck size={14} /> Perfect Match (0.00 {baseUnitName})
                  </span>
                ) : difference < -0.0001 ? (
                  <span className="badge badge-error gap-1 text-xs py-2 px-3 font-semibold text-white">
                    <IconAlertCircle size={14} /> Shortage: {Math.abs(difference).toFixed(2)} {baseUnitName}
                  </span>
                ) : (
                  <span className="badge badge-warning gap-1 text-xs py-2 px-3 font-semibold">
                    <IconAlertTriangle size={14} /> Surplus: +{difference.toFixed(2)} {baseUnitName}
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 text-center text-xs">
              <div className="bg-base-200/60 rounded-lg p-2">
                <span className="text-[10px] text-base-content/50 block">Custody Bal.</span>
                <span className="font-semibold text-base-content font-mono">
                  {initialCustody.toFixed(2)}
                </span>
              </div>
              <div className="bg-base-200/60 rounded-lg p-2">
                <span className="text-[10px] text-info block">(-) POS Sales</span>
                <span className="font-semibold text-info font-mono">
                  {posSaleBaseQty.toFixed(2)}
                </span>
              </div>
              <div className="bg-base-200/60 rounded-lg p-2">
                <span className="text-[10px] text-success block">(-) Returned</span>
                <span className="font-semibold text-success font-mono">
                  {returnBaseQty.toFixed(2)}
                </span>
              </div>
              <div className="bg-base-200/60 rounded-lg p-2">
                <span className="text-[10px] text-warning block">(-) Wastage</span>
                <span className="font-semibold text-warning font-mono">
                  {consumeBaseQty.toFixed(2)}
                </span>
              </div>
              <div className="bg-base-200/60 rounded-lg p-2">
                <span className="text-[10px] text-base-content/50 block">(-) Pend. Sales</span>
                <span className="font-semibold text-base-content/70 font-mono">
                  {pendingSales.toFixed(2)}
                </span>
              </div>
              <div className="bg-primary/10 rounded-lg p-2 border border-primary/20">
                <span className="text-[10px] text-primary font-bold block">(=) Expected</span>
                <span className="font-bold text-primary font-mono">
                  {expectedRemaining.toFixed(2)}
                </span>
              </div>
              <div className="bg-base-200/60 rounded-lg p-2 border border-base-300">
                <span className="text-[10px] text-base-content/70 block">Actual Found</span>
                <span className="font-bold text-base-content font-mono">
                  {actualBaseQty.toFixed(2)}
                </span>
              </div>
            </div>

            {Math.abs(difference) > 0.0001 && (
              <p className="text-[11px] text-base-content/60 italic text-center">
                {difference < -0.0001
                  ? `Shortage of ${Math.abs(difference).toFixed(2)} ${baseUnitName} will be recorded in the reconciliation ledger and adjusted.`
                  : `Surplus of ${difference.toFixed(2)} ${baseUnitName} will be recorded in the reconciliation ledger.`}
              </p>
            )}
          </div>

          {/* Custody Finalize & Close Checkbox */}
          <div className="bg-primary/5 rounded-xl p-3 border border-primary/20 flex items-start gap-3">
            <input
              type="checkbox"
              id="chk_close_custody"
              className="checkbox checkbox-primary checkbox-sm mt-0.5"
              checked={closeCustody}
              onChange={(e) => setCloseCustody(e.target.checked)}
            />
            <label htmlFor="chk_close_custody" className="cursor-pointer text-xs space-y-0.5">
              <span className="font-semibold text-base-content block">
                Finalize & Close Custody (Remove from Product in Custody & Product in Return)
              </span>
              <span className="text-[11px] text-base-content/60 block">
                When checked, this stock record is marked as fully closed and will no longer appear in active custody registers or return dropdowns.
              </span>
            </label>
          </div>

          {/* Modal Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-base-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="btn btn-sm btn-ghost text-xs"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || posSaleExceeds || returnExceeds || consumeExceeds}
              className="btn btn-sm btn-primary text-xs gap-2"
            >
              {isSubmitting ? (
                <>
                  <IconRotate className="animate-spin" size={16} stroke={iconStroke} />
                  Processing Audit & Settlement...
                </>
              ) : (
                <>
                  <IconCheck size={16} stroke={iconStroke} />
                  Submit Audit & Settlement
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </dialog>
  );
}
